/**
 * DietPlanScreen.helpers.js
 *
 * Pure functions, no React. Self-contained by design (see the note in
 * SummaryStep.helpers.js) — no cross-folder imports except the shared
 * color palette, so a folder rename elsewhere in the app can't silently
 * break this screen.
 *
 * The mock regenerate/substitute logic here stands in for UC-03/UC-04's
 * real AI Recommendation Engine and pre-defined substitution chains until
 * those backend pieces exist — swap the relevant TODOs for real API calls
 * once they do.
 *
 * v1.1 changes:
 * - Colors now come from the shared src/components/common/colors.js
 *   (DIET_C_RGB, itself derived from the existing DIET_C token object) —
 *   nothing in colors.js was renamed or removed; a few keys
 *   (primaryLight, primaryTint, accentTint, white, border) were added to
 *   DIET_C to support the PDF's header-depth panel and card/bar tints,
 *   plus a new DIET_C_RGB export for jsPDF. See colors.js §7-8.
 * - `humanize`/`toTitleCase` now correctly title-cases space-separated
 *   strings too (previously only underscore_separated values were fully
 *   capitalized — "Gain weight" stayed "Gain weight" instead of becoming
 *   "Gain Weight").
 * - New `buildPdfFileName(name)` — the downloaded file is now named after
 *   the person, e.g. "Sarah_Kapoor_Diet_Plan.pdf", falling back to
 *   "Diet_Plan.pdf" when no name is available.
 *
 * v1.2 — header revision: the v1.1 header used a diagonal two-tone panel
 * for visual depth, but at these header proportions it rendered as a
 * faint, oddly-placed wedge rather than a deliberate design choice, and
 * the logo badge sat close enough to the header's bottom edge to read as
 * clipping into the page body. Replaced with a single flat header color
 * (renders cleanly at any height), a logo badge with real margin on every
 * side, and — since Name/Age needed to go somewhere anyway — a right-hand
 * "Prepared For" identity panel (Name / Age / Weight) built directly into
 * the header instead of a separate bar below it.
 * - `humanize`/`toTitleCase` now correctly title-cases space-separated
 *   strings too (previously only underscore_separated values were fully
 *   capitalized — "Gain weight" stayed "Gain weight" instead of becoming
 *   "Gain Weight").
 * - generatePlanPdf now accepts a `profile` (Name / Age / Weight, from
 *   Screen 2) and renders a Patient Info bar under the header.
 * - New `buildPdfFileName(name)` — the downloaded file is now named after
 *   the person, e.g. "Sarah_Kapoor_Diet_Plan.pdf", falling back to
 *   "Diet_Plan.pdf" when no name is available.
 * - Header redesign: cooler/deeper green + a subtle duotone diagonal
 *   panel for depth, logo on a white badge for guaranteed legibility, and
 *   a gold accent strip — see the design note in colors.js.
 *
 * v1.3 — header fix + redesign:
 * - Fixed the logo silently not appearing in the downloaded PDF (it
 *   rendered fine in the on-screen header). Root cause: the PDF loaded
 *   the logo via fetch()+FileReader, which can be blocked by a stricter
 *   `connect-src` CSP than the `img-src` an <img> tag falls under — same
 *   asset, different fetch path, different result. Now loads via a plain
 *   `new Image()` (like the on-screen header already does), which jsPDF
 *   accepts directly for addImage.
 * - Replaced the two-column "logo+title on the left / Prepared-For panel
 *   on the right" layout with a single left-aligned column — logo badge,
 *   then title/subtitle/date/Prepared-for stacked beside it — matching
 *   the clean, simple in-app header instead of a layout that needed its
 *   own divider and vertical centering.
 */

import { DEFAULT_DISCLAIMER } from "./DietPlanScreen.constants";
// Adjust the leading "../" count to match wherever this file actually
// lives relative to src/components/common/colors.js.
import { DIET_C_RGB } from "../../../common/colors";
// Adjust this path if MealEngine.js ends up somewhere other than a sibling
// folder to DietPlan/ (e.g. src/components/sections/MealEngine/).
import { pickMealForSlot, buildScaledMealSlot } from "../MealEngine/MealEngine";

// jsPDF-ready RGB palette, sourced from the single shared color file
// (src/components/common/colors.js). `PDF_BRAND` keeps the original local
// key names this file already used internally (`textDark`, `line`) so the
// rest of this file doesn't need touching — they're mapped from the real
// DIET_C_RGB keys (`text`, `border`) here, in one place.
const PDF_BRAND = {
  primary: DIET_C_RGB.primary,
  primaryLight: DIET_C_RGB.primaryLight,
  primaryTint: DIET_C_RGB.primaryTint,
  accent: DIET_C_RGB.accent,
  accentTint: DIET_C_RGB.accentTint,
  textDark: DIET_C_RGB.text,
  textMuted: DIET_C_RGB.textMuted,
  line: DIET_C_RGB.border,
  white: DIET_C_RGB.white,
};

// Loads an image (by URL — typically a bundler-resolved asset import like
// `import logo from ".../YogashastraClear.png"`) and returns an
// HTMLImageElement plus its natural pixel dimensions, for embedding via
// jsPDF's addImage (which accepts an <img> element directly, not just a
// base64 string). Kept separate from generatePlanPdf so a broken/missing
// logo can be caught and skipped there without aborting the whole PDF.
//
// v1.3 fix: this used to fetch() the asset and read it back as a base64
// data URL. That's why the on-screen header (a plain <img src="..."> in
// the styled Header component) always showed the logo fine, while the
// PDF's logo silently vanished — a same-origin asset can be blocked by a
// stricter `connect-src` (which fetch() falls under) while still being
// allowed under `img-src` (which <img> — and jsPDF's own internal
// canvas draw of an <img> element — falls under). Loading via `new
// Image()` instead sidesteps that mismatch entirely and matches how the
// header already loads the same file successfully.
async function loadImageAsDataUrl(url) {
  const img = await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Could not load logo image"));
    image.src = url;
  });
  const formatMatch = /^data:image\/(png|jpe?g)/i.exec(url) || /\.(png|jpe?g)(?:[?#]|$)/i.exec(url);
  const format = formatMatch ? formatMatch[1].toUpperCase().replace("JPG", "JPEG") : "PNG";
  return { image: img, width: img.naturalWidth, height: img.naturalHeight, format };
}

// Title-cases a value regardless of whether words are separated by
// underscores ("gain_weight"), spaces ("Gain weight"), or a mix. Used for
// anything user-facing that comes back from the backend/DB as a raw enum
// or a partially-capitalized string: goal labels, condition names, and
// (new) the person's name for the PDF's Patient Info bar and filename.
//
// Exported as both `toTitleCase` (generic, reads clearly at any call
// site) and `humanize` (kept for backward compatibility with existing
// imports elsewhere in the app) — same function, two names.
export function toTitleCase(value) {
  if (value === null || value === undefined || value === "") return "";
  return String(value)
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}
export const humanize = toTitleCase;

// Builds a filesystem-safe PDF filename from the person's name, e.g.
// "Sarah Kapoor" -> "Sarah_Kapoor_Diet_Plan.pdf". Title-cases the name
// first (so "sarah kapoor" and "SARAH KAPOOR" both produce the same,
// properly-capitalized filename), strips anything that isn't a letter,
// number, or space, then joins words with underscores.
//
// Falls back to "Diet_Plan.pdf" when no usable name is available (Name is
// optional per FSD Screen 2), so the download is never left with an
// ambiguous "_Diet_Plan.pdf".
export function buildPdfFileName(name) {
  const cleaned = toTitleCase(name)
    .replace(/[^a-zA-Z0-9\s]/g, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .join("_");
  return cleaned ? `${cleaned}_Diet_Plan.pdf` : "Diet_Plan.pdf";
}

// UC-04 Main Flow step 2: "displays the pre-defined substitution chain
// for that ingredient ... filtered to exclude the user's
// allergens/dislikes." dislikes is the array of food ids/labels from
// Screen 9 — matched case-insensitively against the chain's labels.
export function filterSubstitutionChain(chain, dislikes = []) {
  const dislikeSet = new Set((dislikes || []).map((d) => String(d).toLowerCase().replace(/_/g, " ")));
  return (chain || []).filter((item) => !dislikeSet.has(String(item).toLowerCase().replace(/_/g, " ")));
}

// TODO: replace with a real UC-03 call to the AI Recommendation Engine
// (enforcing the ±10%-of-meal-target / ±5%-of-daily-target tolerance from
// FSD v2.1 DEC-14 / Policy §15 server-side, which this mock does not).
//
// `ctx` is the same shape MealEngine.buildMealContext() produces — passed
// in by DietPlanScreen.js's `mealContext` prop (built once from the
// wizard's payload) whenever this local mock is used instead of a real
// `onRegenerateMeal`. It's what lets regeneration pick a dish that's
// actually still diet-type/gluten/dislike-eligible for this user, from
// MealCatalog.js's per-slot candidates, rather than cycling through a
// fixed name list with randomly nudged macros.
//
// `meal.id` is the slot ("breakfast", "lunch", ...); `meal.dishId` is the
// specific catalog dish currently shown (see MealEngine.buildScaledMealSlot)
// — excluded here so regenerating never hands back the same dish. The new
// dish is rescaled to the *same* calorie/protein figures the current meal
// already has, so a regenerate swaps what you're eating without silently
// changing how much of your daily target that slot represents.
//
// Resolves to { meal, warning }, not a bare meal — matches the real
// endpoint's contract so DietPlanScreen doesn't need to change again once
// UC-03 exists. `warning` mirrors DEC-16's "warn instead of inventing
// food" language for the (rare) case a regenerated meal can't be kept
// within tolerance; this mock never sets it today.
export function mockRegenerateMeal(meal, ctx = {}) {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      const slotId = meal.id;
      const excludeIds = new Set(meal.dishId ? [meal.dishId] : []);
      const nextDish = pickMealForSlot(slotId, ctx, excludeIds);

      if (!nextDish) {
        reject(new Error("NO_ALTERNATIVE"));
        return;
      }

      const target = { calories: meal.calories, proteinG: meal.proteinG };
      resolve({
        meal: buildScaledMealSlot(slotId, nextDish, target, meal.time),
        warning: null,
      });
    }, 900);
  });
}

// TODO: replace with a real UC-04 call. This mock recalculates the meal's
// totals by removing the old ingredient's mock macro contribution and
// adding the substitute's, so the card visibly updates.
//
// Per FSD v2.1 DEC-16 / Policy §15: "Replacement must come from an
// approved substitution group. Recalculate meal + daily totals
// immediately. If daily calories move outside ±5% or protein below
// minimum, auto-rebalance the next eligible meal; if no safe rebalance
// exists, warn instead of inventing food." Resolves to
// { meal, rebalancedMeals, warning } so DietPlanScreen can apply an
// auto-rebalanced sibling meal or surface the warning — the real backend
// owns the ±5%/protein-floor check and rebalance decision; this mock
// never triggers either branch, it just shapes the response the same way.
// Deterministic stand-in for "look up the substitute's real macro
// delta in the Nutrition Database." Same (old ingredient, substitute)
// pair always produces the same nudge, so re-selecting the same
// substitute twice doesn't jitter the totals around — a small honesty
// improvement over a random delta, though still not real ingredient-level
// nutrition data.
function pseudoMacroDelta(seed, maxAbs) {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  const normalized = (Math.abs(hash) % (maxAbs * 2 + 1)) - maxAbs;
  return normalized;
}

export function mockReplaceIngredient(meal, ingredientId, substituteName) {
  return new Promise((resolve) => {
    setTimeout(() => {
      const ingredients = meal.ingredients.map((ing) =>
        ing.id === ingredientId
          ? { ...ing, name: substituteName, substitutionChain: ing.substitutionChain }
          : ing
      );
      const delta = pseudoMacroDelta(`${ingredientId}:${substituteName}`, 15);
      resolve({
        meal: {
          ...meal,
          ingredients,
          calories: Math.max(120, meal.calories + delta),
        },
        rebalancedMeals: [],
        warning: null,
      });
    }, 600);
  });
}

// UC-05: builds a real, downloadable PDF client-side. There's no server
// PDF Generation Service specified yet, so this stands in for it — jsPDF
// must be installed (`npm install jspdf`) for this to work. Dynamically
// imported so it's only pulled into the bundle when someone actually
// clicks Download PDF.
//
// Branded layout: a solid header with the logo on a fully-contained white
// badge, the plan title, and a right-hand "Prepared For" panel (Name /
// Age / Weight); a health-summary "chip" row; meal blocks with a left
// accent bar; a bulleted tips section; and a repeated footer (brand line
// + page number) on every page.
//
// v1.2 header redesign: the previous version tried a diagonal two-tone
// panel for "depth," but at these header proportions it rendered as a
// faint, oddly-placed wedge rather than a deliberate design element, and
// the logo badge sat close enough to the header's bottom edge that it
// could read as clipping into the body. This version uses a single flat
// header color (simpler, and reliably renders cleanly at any header
// height), gives the logo badge real margin on all sides so it's never
// ambiguous whether it's "in" the header, and — since the ask was to
// surface Name/Age anyway — uses that space productively as a right-hand
// identity panel instead of a decorative effect that wasn't landing.
//
// @param {object} plan
// @param {object} [options]
// @param {string} [options.logoUrl] - URL/path to the logo image (e.g. a
//   bundler-resolved import of src/assets/YogashastraClear.png). Optional
//   and best-effort: if it fails to load, the PDF still generates, just
//   without the logo — a missing asset should never block a download.
// @param {object} [options.profile] - `{ name, age, weight, weightUnit }`
//   collected on Screen 2 (Basic Information). All fields optional per
//   FSD — only the fields that are present render in the header's
//   "Prepared For" panel. `name` is also used by `buildPdfFileName` for
//   the download's filename; pass it here (or as `plan.profile`) rather
//   than baking it into `plan` itself, since identity data and the
//   generated plan are logically separate (mirrors how `dislikes` is a
//   sibling prop on DietPlanScreen, not part of `plan`).
// @param {number[]} [options.brandColor] - [r,g,b] override for the
//   header/section-title color, in case the shared palette ever needs a
//   one-off override for a specific export.
// @param {number[]} [options.brandTint] - [r,g,b] override for the
//   health-summary chip background.
// @param {number[]} [options.brandAccent] - [r,g,b] override for the
//   accent color (meal-card bar, tip bullets, underline).
// export async function generatePlanPdf(plan, options = {}) {
//   const { jsPDF } = await import("jspdf");
//   const doc = new jsPDF({ unit: "pt", format: "a4" });
//   const marginX = 40;
//   const pageWidth = doc.internal.pageSize.getWidth();
//   const pageHeight = doc.internal.pageSize.getHeight();
//   const contentWidth = pageWidth - marginX * 2;

//   const brand = {
//     primary: options.brandColor || PDF_BRAND.primary,
//     primaryTint: options.brandTint || PDF_BRAND.primaryTint,
//     accent: options.brandAccent || PDF_BRAND.accent,
//     accentTint: options.brandAccentTint || PDF_BRAND.accentTint,
//     white: PDF_BRAND.white,
//   };

//   // Name/Age/Weight from Screen 2 — accepted either as `options.profile`
//   // (preferred call shape) or `plan.profile` (in case a caller already
//   // attaches it to the plan object). Every field is optional.
//   const profile = options.profile || plan?.profile || {};

//   let logo = null;
//   if (options.logoUrl) {
//     try {
//       logo = await loadImageAsDataUrl(options.logoUrl);
//     } catch (err) {
//       // eslint-disable-next-line no-console
//       console.warn("[generatePlanPdf] Logo failed to load, continuing without it:", err);
//     }
//   }

//   // Whether there's any identity info to show at all — determines the
//   // header height (taller header = room for the extra "Prepared for"
//   // line) and whether that line renders.
//   const hasProfileInfo = Boolean(profile.name || profile.age || profile.weight);
//   const HEADER_HEIGHT = hasProfileInfo ? 156 : 108;
//   let y = HEADER_HEIGHT + 26;

//   // v1.3 header redesign: the v1.2 header split into two columns — logo
//   // + title on the left, a separate "Prepared For" panel on the right —
//   // which needed its own divider and its own vertical centering, and
//   // stopped looking like one deliberate block once a page had a longer
//   // title or name. This version keeps everything the on-screen header
//   // already does well: one flat header color, a gold accent strip, and a
//   // single left-aligned column (logo badge, then title/subtitle/date
//   // stacked beside it) — and simply continues that same column with a
//   // "Prepared for" line instead of branching into a second layout.
//   const drawHeader = () => {
//     doc.setFillColor(...brand.primary);
//     doc.rect(0, 0, pageWidth, HEADER_HEIGHT, "F");

//     // Gold accent strip along the header's bottom edge, tying back to the
//     // brand's saffron accent used throughout the rest of the document.
//     doc.setFillColor(...brand.accent);
//     doc.rect(0, HEADER_HEIGHT - 3, pageWidth, 3, "F");

//     // ── Logo badge ───────────────────────────────────────────────────
//     // White badge behind the logo, with real margin on every side so
//     // it's unambiguously "inside" the header, top-anchored to match the
//     // on-screen header rather than vertically centered (which would
//     // drift as HEADER_HEIGHT changes with/without profile info).
//     const badgeHeight = 56;
//     const badgeY = 24;
//     let textX = marginX;

//     if (logo) {
//       // Size the badge to the logo's own aspect ratio instead of forcing
//       // it into a fixed square: this logo is a wide icon+wordmark
//       // lockup, and shrinking it to fit a 56×56 square (see the
//       // overflow bug above) made the wordmark tiny and hard to read.
//       // Height-capped, width-capped (so an unusually wide logo still
//       // can't dominate the header row).
//       const logoPadding = 10;
//       const maxLogoH = badgeHeight - logoPadding * 2;
//       const maxBadgeWidth = 170;
//       const maxLogoW = maxBadgeWidth - logoPadding * 2;
//       const aspect = logo.width / logo.height;
//       let logoH = maxLogoH;
//       let logoW = logoH * aspect;
//       if (logoW > maxLogoW) {
//         logoW = maxLogoW;
//         logoH = logoW / aspect;
//       }
//       const badgeWidth = logoW + logoPadding * 2;

//       doc.setFillColor(...brand.white);
//       doc.roundedRect(marginX, badgeY, badgeWidth, badgeHeight, 10, 10, "F");
//       doc.addImage(
//         logo.image,
//         logo.format,
//         marginX + (badgeWidth - logoW) / 2,
//         badgeY + (badgeHeight - logoH) / 2,
//         logoW,
//         logoH
//       );
//       textX = marginX + badgeWidth + 18;
//     }

//     // ── Title / subtitle / date / Prepared-for, one stacked column ───
//     const titleMaxWidth = pageWidth - textX - marginX;
//     doc.setTextColor(...brand.white);
//     doc.setFont("helvetica", "bold");
//     doc.setFontSize(19);
//     let ty = 42;
//     const titleLines = doc.splitTextToSize("Your Personalized Diet Plan", titleMaxWidth);
//     titleLines.slice(0, 2).forEach((line) => {
//       doc.text(line, textX, ty);
//       ty += 21;
//     });

//     doc.setFont("helvetica", "normal");
//     doc.setFontSize(10.5);
//     doc.text("YogaShastra AI Diet Planner", textX, ty);
//     ty += 14;

//     doc.setFontSize(8.5);
//     doc.text(`Generated ${new Date(plan?.generatedAt || Date.now()).toLocaleDateString()}`, textX, ty);
//     ty += 14;

//     if (hasProfileInfo) {
//       // Thin divider (muted sage, not a hard white line) separating the
//       // brand block from the patient line, then a small gold label and
//       // the name/age/weight in one readable row.
//       const dividerWidth = Math.min(titleMaxWidth, 260);
//       doc.setDrawColor(160, 205, 185);
//       doc.setLineWidth(0.75);
//       doc.line(textX, ty + 4, textX + dividerWidth, ty + 4);
//       ty += 20;

//       doc.setFont("helvetica", "bold");
//       doc.setFontSize(7.5);
//       doc.setTextColor(...brand.accent);
//       doc.text("PREPARED FOR", textX, ty);
//       ty += 13;

//       const detailParts = [];
//       if (profile.name) detailParts.push(toTitleCase(profile.name));
//       if (profile.age !== undefined && profile.age !== null && profile.age !== "") {
//         detailParts.push(`Age ${profile.age}`);
//       }
//       if (profile.weight !== undefined && profile.weight !== null && profile.weight !== "") {
//         detailParts.push(`${profile.weight} ${profile.weightUnit || "kg"}`);
//       }
//       doc.setFont("helvetica", "bold");
//       doc.setFontSize(12);
//       doc.setTextColor(...brand.white);
//       const detailLines = doc.splitTextToSize(detailParts.join("   ·   "), titleMaxWidth);
//       doc.text(detailLines[0], textX, ty);
//     }

//     doc.setTextColor(...PDF_BRAND.textDark);
//   };

//   drawHeader();

//   const ensureSpace = (needed) => {
//     if (y + needed > pageHeight - 50) {
//       doc.addPage();
//       y = 40;
//     }
//   };

//   const sectionTitle = (title) => {
//     ensureSpace(26);
//     doc.setTextColor(...brand.primary);
//     doc.setFont("helvetica", "bold");
//     doc.setFontSize(13);
//     doc.text(title, marginX, y);
//     doc.setDrawColor(...brand.accent);
//     doc.setLineWidth(1.5);
//     doc.line(marginX, y + 5, marginX + 32, y + 5);
//     doc.setTextColor(...PDF_BRAND.textDark);
//     y += 22;
//   };

//   const writeLine = (
//     text,
//     { size = 10.5, bold = false, gap = 15, color = PDF_BRAND.textDark, x = marginX, maxWidth = contentWidth } = {}
//   ) => {
//     doc.setFont("helvetica", bold ? "bold" : "normal");
//     doc.setFontSize(size);
//     doc.setTextColor(...color);
//     const lines = doc.splitTextToSize(text, maxWidth);
//     lines.forEach((line) => {
//       ensureSpace(gap);
//       doc.text(line, x, y);
//       y += gap;
//     });
//     doc.setTextColor(...PDF_BRAND.textDark);
//   };

//   // ── Health summary chips ────────────────────────────────────────────
//   sectionTitle("Health Summary");
//   const hs = plan?.healthSummary || {};
//   const chips = [
//     { label: "Calories", unit: "kcal/day", value: hs.calories != null ? `${hs.calories}` : "—", numeric: true },
//     { label: "Protein", unit: "g/day", value: hs.proteinG != null ? `${hs.proteinG}` : "—", numeric: true },
//     { label: "Water", unit: "L/day", value: hs.waterL != null ? `${hs.waterL}` : "—", numeric: true },
//     { label: "BMI", unit: "", value: hs.bmi != null ? `${hs.bmi}` : "—", numeric: true },
//     { label: "Goal", unit: "", value: hs.goal ? toTitleCase(hs.goal) : "—", numeric: false },
//   ];
//   const chipGap = 8;
//   const chipWidth = (contentWidth - chipGap * (chips.length - 1)) / chips.length;
//   const chipHeight = 50;
//   ensureSpace(chipHeight + 16);
//   chips.forEach((chip, i) => {
//     const cx = marginX + i * (chipWidth + chipGap);
//     doc.setFillColor(...brand.primaryTint);
//     doc.roundedRect(cx, y, chipWidth, chipHeight, 5, 5, "F");

//     doc.setFont("helvetica", "bold");
//     doc.setFontSize(chip.numeric ? 13 : 9.5);
//     doc.setTextColor(...brand.primary);
//     doc.text(chip.value, cx + chipWidth / 2, y + 22, { align: "center", maxWidth: chipWidth - 8 });

//     doc.setFont("helvetica", "normal");
//     doc.setFontSize(7.5);
//     doc.setTextColor(...PDF_BRAND.textMuted);
//     doc.text(chip.unit ? `${chip.label} (${chip.unit})` : chip.label, cx + chipWidth / 2, y + 36, {
//       align: "center",
//       maxWidth: chipWidth - 8,
//     });
//   });
//   y += chipHeight + 28;

//   // ── Meals ────────────────────────────────────────────────────────────
//   if ((plan?.mealSlots || []).length > 0) {
//     sectionTitle("Today's Meals");
//   }
//   (plan?.mealSlots || []).forEach((meal) => {
//     ensureSpace(40);
//     const mealStartY = y - 4;

//     writeLine(`${(meal.slotLabel || "").toUpperCase()}${meal.time ? `  ·  ${meal.time}` : ""}`, {
//       size: 8,
//       bold: true,
//       gap: 12,
//       color: PDF_BRAND.textMuted,
//     });
//     writeLine(meal.name, { size: 12.5, bold: true, gap: 16 });
//     writeLine(
//       `${meal.calories} kcal   ·   ${meal.proteinG}g protein   ·   ${meal.carbsG}g carbs   ·   ${meal.fatG}g fat`,
//       { size: 9, gap: 14, color: PDF_BRAND.textMuted }
//     );

//     (meal.ingredients || []).forEach((ing) => {
//       ensureSpace(13);
//       doc.setFillColor(...brand.accent);
//       doc.circle(marginX + 3, y - 3, 1.4, "F");
//       doc.setFont("helvetica", "normal");
//       doc.setFontSize(9.5);
//       doc.setTextColor(...PDF_BRAND.textDark);
//       doc.text(ing.name, marginX + 10, y);
//       y += 13;
//     });

//     // Left accent bar for this meal's block, drawn last so its height
//     // can span exactly what the meal ended up rendering as.
//     doc.setDrawColor(...brand.accent);
//     doc.setLineWidth(2.5);
//     doc.line(marginX - 8, mealStartY, marginX - 8, y - 8);

//     y += 14;
//   });

//   // ── Daily tips ───────────────────────────────────────────────────────
//   if ((plan?.dailyTips || []).length > 0) {
//     sectionTitle("Daily Tips");
//     plan.dailyTips.forEach((tip) => {
//       const lines = doc.splitTextToSize(tip, contentWidth - 14);
//       ensureSpace(14);
//       doc.setFillColor(...brand.primary);
//       doc.circle(marginX + 3, y - 3, 1.6, "F");
//       doc.setFont("helvetica", "normal");
//       doc.setFontSize(10);
//       doc.setTextColor(...PDF_BRAND.textDark);
//       lines.forEach((line) => {
//         ensureSpace(14);
//         doc.text(line, marginX + 10, y);
//         y += 14;
//       });
//     });
//     y += 6;
//   }

//   // ── Disclaimer ───────────────────────────────────────────────────────
//   ensureSpace(40);
//   doc.setDrawColor(...PDF_BRAND.line);
//   doc.setLineWidth(0.75);
//   doc.line(marginX, y, marginX + contentWidth, y);
//   y += 14;
//   writeLine(plan?.disclaimer || DEFAULT_DISCLAIMER, { size: 8, gap: 11, color: PDF_BRAND.textMuted });

//   // ── Footer on every page ────────────────────────────────────────────
//   const pageCount = doc.internal.getNumberOfPages();
//   for (let i = 1; i <= pageCount; i += 1) {
//     doc.setPage(i);
//     doc.setDrawColor(...PDF_BRAND.line);
//     doc.setLineWidth(0.5);
//     doc.line(marginX, pageHeight - 34, pageWidth - marginX, pageHeight - 34);
//     doc.setFont("helvetica", "normal");
//     doc.setFontSize(8);
//     doc.setTextColor(...PDF_BRAND.textMuted);
//     doc.text("YogaShastra AI Diet Planner", marginX, pageHeight - 20);
//     doc.text(`Page ${i} of ${pageCount}`, pageWidth - marginX, pageHeight - 20, { align: "right" });
//   }

//   return doc;
// }
export async function generatePlanPdf(plan, options = {}) {
  const { jsPDF } = await import("jspdf");

  // ============================================================
  // YogaShastra Premium 3-Page AI Diet Planner PDF
  //
  // PAGE 1:
  // Personalized Nutrition Plan
  // Health Snapshot
  // Goal
  // Today's Meal Timeline
  //
  // PAGE 2:
  // Detailed Meal Cards
  // Ingredients
  // Nutrition
  // Why It Fits
  //
  // PAGE 3:
  // Wellness Guide
  // Daily Tips
  // AI Note
  // Quick Reference
  // Disclaimer
  // ============================================================

  const doc = new jsPDF({
    unit: "pt",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // ------------------------------------------------------------
  // PREMIUM YOGASHASTRA COLORS
  // ------------------------------------------------------------

  const C = {
    ivory: [248, 245, 238],
    sage: [113, 133, 111],
    sageDark: [64, 84, 66],
    sageTint: [238, 242, 235],
    gold: [185, 154, 91],
    goldTint: [247, 241, 226],
    charcoal: [41, 50, 44],
    muted: [110, 116, 110],
    white: [255, 255, 255],
    border: [225, 221, 211],
    soft: [241, 238, 230],
  };

  const brand = {
    sage: options.brandColor || C.sage,
    sageDark: C.sageDark,
    sageTint: options.brandTint || C.sageTint,
    gold: options.brandAccent || C.gold,
    goldTint: options.brandAccentTint || C.goldTint,
    ivory: C.ivory,
    charcoal: C.charcoal,
    muted: C.muted,
    white: C.white,
    border: C.border,
    soft: C.soft,
  };

  // ------------------------------------------------------------
  // LAYOUT
  // ------------------------------------------------------------

  const marginX = 48;
  const contentWidth = pageWidth - marginX * 2;

  const profile = options.profile || plan?.profile || {};
  const hs = plan?.healthSummary || {};

  const meals = Array.isArray(plan?.mealSlots)
    ? plan.mealSlots
    : [];

  const tips = Array.isArray(plan?.dailyTips)
    ? plan.dailyTips
    : [];

  // ------------------------------------------------------------
  // LOGO
  // ------------------------------------------------------------

  let logo = null;

  if (options.logoUrl) {
    try {
      logo = await loadImageAsDataUrl(options.logoUrl);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(
        "[generatePlanPdf] Logo failed to load, continuing without it:",
        err
      );
    }
  }

  // ------------------------------------------------------------
  // HELPERS
  // ------------------------------------------------------------

  const setFill = (rgb) => {
    doc.setFillColor(...rgb);
  };

  const setText = (rgb) => {
    doc.setTextColor(...rgb);
  };

  const setDraw = (rgb) => {
    doc.setDrawColor(...rgb);
  };

  const roundedCard = (
    x,
    y,
    w,
    h,
    fill = brand.white,
    radius = 10,
    border = null
  ) => {
    setFill(fill);

    if (border) {
      setDraw(border);
      doc.setLineWidth(0.6);

      doc.roundedRect(
        x,
        y,
        w,
        h,
        radius,
        radius,
        "FD"
      );
    } else {
      doc.roundedRect(
        x,
        y,
        w,
        h,
        radius,
        radius,
        "F"
      );
    }
  };

  const drawText = (
    value,
    x,
    y,
    size = 10,
    style = "normal",
    color = brand.charcoal,
    textOptions = {}
  ) => {
    doc.setFont("helvetica", style);
    doc.setFontSize(size);
    setText(color);

    doc.text(
      String(value ?? ""),
      x,
      y,
      textOptions
    );
  };

  const wrapText = (
    value,
    x,
    y,
    maxWidth,
    size = 10,
    style = "normal",
    color = brand.charcoal,
    lineGap = 14
  ) => {
    doc.setFont("helvetica", style);
    doc.setFontSize(size);
    setText(color);

    const lines = doc.splitTextToSize(
      String(value ?? ""),
      maxWidth
    );

    lines.forEach((line, index) => {
      doc.text(
        line,
        x,
        y + index * lineGap
      );
    });

    return (
      y +
      Math.max(0, lines.length - 1) * lineGap
    );
  };

  // Counts how many lines `wrapText` would need for a given string/width/
  // font, without drawing anything — lets callers size a container (e.g.
  // a meal card) to fit the text *before* drawing the container's
  // background, instead of guessing a fixed height and hoping the text
  // fits inside it.
  const measureWrappedLineCount = (value, maxWidth, size = 10, style = "normal") => {
    doc.setFont("helvetica", style);
    doc.setFontSize(size);
    const lines = doc.splitTextToSize(String(value ?? ""), maxWidth);
    return Math.max(1, lines.length);
  };

  const formatDate = (value) => {
    const date = value
      ? new Date(value)
      : new Date();

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  // ------------------------------------------------------------
  // PAGE BACKGROUND
  // ------------------------------------------------------------

  const drawPageBackground = () => {
    setFill(brand.ivory);

    doc.rect(
      0,
      0,
      pageWidth,
      pageHeight,
      "F"
    );

    // Very subtle decorative background circles.
    setFill([242, 239, 231]);

    doc.circle(
      pageWidth - 22,
      35,
      38,
      "F"
    );

    doc.circle(
      8,
      pageHeight - 28,
      48,
      "F"
    );
  };

  // ------------------------------------------------------------
  // FOOTER
  // ------------------------------------------------------------

  const drawFooter = (pageNumber, totalPages = 3) => {
    setDraw(brand.border);

    doc.setLineWidth(0.5);

    doc.line(
      marginX,
      pageHeight - 34,
      pageWidth - marginX,
      pageHeight - 34
    );

    drawText(
      "YOGASHASTRA  ·  AI DIET PLANNER",
      marginX,
      pageHeight - 19,
      7.5,
      "bold",
      brand.sageDark
    );

    drawText(
      `PAGE ${pageNumber} OF ${totalPages}`,
      pageWidth - marginX,
      pageHeight - 19,
      7.5,
      "normal",
      brand.muted,
      {
        align: "right",
      }
    );
  };

  // ------------------------------------------------------------
  // BRAND MARK
  // ------------------------------------------------------------

  const drawBrandMark = (
    x,
    y,
    w = 128,
    h = 38
  ) => {
    roundedCard(
      x,
      y,
      w,
      h,
      brand.white,
      8,
      brand.border
    );

    if (logo) {
      const padding = 7;

      const maxW = w - padding * 2;
      const maxH = h - padding * 2;

      const aspect =
        logo.width /
        Math.max(1, logo.height);

      let logoW = maxW;
      let logoH = logoW / aspect;

      if (logoH > maxH) {
        logoH = maxH;
        logoW = logoH * aspect;
      }

      doc.addImage(
        logo.image,
        logo.format,
        x + (w - logoW) / 2,
        y + (h - logoH) / 2,
        logoW,
        logoH
      );
    } else {
      drawText(
        "YOGASHASTRA",
        x + w / 2,
        y + 23,
        10,
        "bold",
        brand.sageDark,
        {
          align: "center",
        }
      );
    }
  };

  // ------------------------------------------------------------
  // SECTION HEADING
  // ------------------------------------------------------------

  const drawSectionHeading = (
    title,
    subtitle,
    y
  ) => {
    drawText(
      title,
      marginX,
      y,
      18,
      "bold",
      brand.charcoal
    );

    if (subtitle) {
      wrapText(
        subtitle,
        marginX,
        y + 18,
        contentWidth,
        9.5,
        "normal",
        brand.muted,
        13
      );
    }

    setFill(brand.gold);

    doc.roundedRect(
      marginX,
      y + (subtitle ? 34 : 26),
      34,
      3,
      1.5,
      1.5,
      "F"
    );

    return y + (subtitle ? 52 : 42);
  };

  // ------------------------------------------------------------
  // PROFILE
  // ------------------------------------------------------------

  const profileParts = [];

  if (profile.name) {
    profileParts.push(
      toTitleCase(profile.name)
    );
  }

  if (
    profile.age !== undefined &&
    profile.age !== null &&
    profile.age !== ""
  ) {
    profileParts.push(
      `Age ${profile.age}`
    );
  }

  if (
    profile.weight !== undefined &&
    profile.weight !== null &&
    profile.weight !== ""
  ) {
    profileParts.push(
      `${profile.weight} ${
        profile.weightUnit || "kg"
      }`
    );
  }

  const preparedFor =
    profileParts.join("  ·  ");

  const goal = hs.goal
    ? toTitleCase(hs.goal)
    : "—";

  // ============================================================
  // PAGE 1
  // ============================================================

  drawPageBackground();

  drawBrandMark(
    marginX,
    30,
    128,
    38
  );

  // Date badge
  roundedCard(
    pageWidth - marginX - 138,
    34,
    138,
    28,
    brand.goldTint,
    14
  );

  drawText(
    formatDate(
      plan?.generatedAt || Date.now()
    ),
    pageWidth - marginX - 69,
    52,
    8.5,
    "bold",
    brand.sageDark,
    {
      align: "center",
    }
  );

  // Hero
  drawText(
    "Your Personalized",
    marginX,
    105,
    25,
    "bold",
    brand.charcoal
  );

  drawText(
    "Nutrition Plan",
    marginX,
    134,
    25,
    "bold",
    brand.charcoal
  );

  drawText(
    "A calm, practical daily plan designed around your nutrition goal.",
    marginX,
    157,
    10.5,
    "normal",
    brand.muted
  );

  // Prepared For
  roundedCard(
    marginX,
    181,
    contentWidth,
    58,
    brand.white,
    10,
    brand.border
  );

  drawText(
    "PREPARED FOR",
    marginX + 14,
    200,
    7.5,
    "bold",
    brand.muted
  );

  drawText(
    preparedFor || "Your personalized plan",
    marginX + 14,
    222,
    12,
    "bold",
    brand.charcoal
  );

  // Goal badge
  roundedCard(
    pageWidth - marginX - 125,
    194,
    111,
    31,
    brand.sageTint,
    15
  );

  drawText(
    goal,
    pageWidth - marginX - 69.5,
    214,
    9,
    "bold",
    brand.sageDark,
    {
      align: "center",
    }
  );

  let y = 267;

  y = drawSectionHeading(
    "Your Health Snapshot",
    "Your key daily targets at a glance.",
    y
  );

  // Health metrics
  const metricGap = 8;

  const metricW =
    (contentWidth -
      metricGap * 3) /
    4;

  const metricH = 72;

  const metrics = [
    {
      value:
        hs.calories != null
          ? `${hs.calories}`
          : "—",
      label: "CALORIES",
      unit: "kcal / day",
    },
    {
      value:
        hs.proteinG != null
          ? `${hs.proteinG} g`
          : "—",
      label: "PROTEIN",
      unit: "per day",
    },
    {
      value:
        hs.waterL != null
          ? `${hs.waterL} L`
          : "—",
      label: "WATER",
      unit: "per day",
    },
    {
      value:
        hs.bmi != null
          ? `${hs.bmi}`
          : "—",
      label: "BMI",
      unit: "",
    },
  ];

  metrics.forEach((metric, index) => {
    const x =
      marginX +
      index *
        (metricW + metricGap);

    roundedCard(
      x,
      y,
      metricW,
      metricH,
      brand.white,
      10,
      brand.border
    );

    drawText(
      metric.value,
      x + metricW / 2,
      y + 30,
      16,
      "bold",
      brand.charcoal,
      {
        align: "center",
      }
    );

    drawText(
      metric.label,
      x + metricW / 2,
      y + 49,
      7,
      "bold",
      brand.sageDark,
      {
        align: "center",
      }
    );

    if (metric.unit) {
      drawText(
        metric.unit,
        x + metricW / 2,
        y + 61,
        6.8,
        "normal",
        brand.muted,
        {
          align: "center",
        }
      );
    }
  });

  y += metricH + 20;

  // Goal
  roundedCard(
    marginX,
    y,
    contentWidth,
    60,
    brand.sageTint,
    10
  );

  drawText(
    "PRIMARY GOAL",
    marginX + 14,
    y + 20,
    7.5,
    "bold",
    brand.muted
  );

  drawText(
    goal,
    marginX + 14,
    y + 43,
    16,
    "bold",
    brand.sageDark
  );

  y += 82;

  y = drawSectionHeading(
    "Today at a Glance",
    "Your meal rhythm from morning to night.",
    y
  );

  // Bug fix: this used to be `meals.slice(0, 4)`, silently dropping any
  // meal past the 4th (e.g. Dinner, on a 5-6 meal plan) even though the
  // card below already sizes itself to `timeline.length` and had room for
  // all of them. No cap needed — show every meal in the plan.
  const timeline = meals;

  const timelineH = Math.max(
    92,
    timeline.length * 54 + 22
  );

  roundedCard(
    marginX,
    y,
    contentWidth,
    timelineH,
    brand.white,
    10,
    brand.border
  );

  const lineX =
    marginX + 28;

  if (timeline.length > 1) {
    setDraw(brand.border);
    doc.setLineWidth(1.2);

    doc.line(
      lineX,
      y + 28,
      lineX,
      y + timeline.length * 54
    );
  }

  timeline.forEach(
    (meal, index) => {
      const rowY =
        y + 19 + index * 54;

      setFill(brand.sage);

      doc.circle(
        lineX,
        rowY + 5,
        5,
        "F"
      );

      drawText(
        meal?.time || "—",
        marginX + 46,
        rowY + 8,
        8,
        "bold",
        brand.gold
      );

      drawText(
        (
          meal?.slotLabel ||
          "MEAL"
        ).toUpperCase(),
        marginX + 100,
        rowY + 8,
        7.5,
        "bold",
        brand.sageDark
      );

      drawText(
        meal?.name || "Meal",
        marginX + 100,
        rowY + 25,
        10.5,
        "bold",
        brand.charcoal
      );

      const kcal =
        meal?.calories != null
          ? `${meal.calories} kcal`
          : "";

      drawText(
        kcal,
        pageWidth - marginX - 12,
        rowY + 25,
        8,
        "normal",
        brand.muted,
        {
          align: "right",
        }
      );
    }
  );

  y += timelineH + 18;

  // Daily focus
  roundedCard(
    marginX,
    y,
    contentWidth,
    55,
    brand.soft,
    10
  );

  drawText(
    "YOUR DAILY FOCUS",
    marginX + 14,
    y + 19,
    7.5,
    "bold",
    brand.sageDark
  );

  wrapText(
    "Protein-rich meals + adequate hydration + consistent meal timing.",
    marginX + 14,
    y + 38,
    contentWidth - 28,
    9.5,
    "normal",
    brand.charcoal,
    12
  );

  // Footer is now drawn once at the very end, across every real page (see
  // below) — a fixed drawFooter(1)/(2)/(3) here assumed exactly 3 pages,
  // which the pagination added above for the meal cards can now exceed.

  // ============================================================
  // PAGE 2
  // ============================================================

  doc.addPage();

  drawPageBackground();

  drawBrandMark(
    marginX,
    30,
    128,
    38
  );

  y = 105;

  y = drawSectionHeading(
    "Your Meals, With Intention",
    "Simple Indian meals, clear nutrition information and an easy rhythm for the day.",
    y
  );

  // Bug fix: this used to be `meals.slice(0, 4)`, dropping any meal past
  // the 4th from the detailed cards page entirely (matching the Page 1
  // timeline bug above) — same fix, show every meal.
  const mealCards = meals;

  const getIngredientNames =
    (meal) =>
      (meal?.ingredients || [])
        .map(
          (ingredient) =>
            ingredient?.name
        )
        .filter(Boolean)
        .join("  ·  ");

  const getWhyItFits =
    (meal, index) => {
      if (meal?.whyItFits) {
        return meal.whyItFits;
      }

      // Keyed by slotLabel rather than raw position now that a plan can
      // have 3-6 meals in any combination (see MealEngine.js) — a
      // position-indexed list would silently run out past the 4th meal
      // and fall through to the generic default for every snack slot.
      const bySlotLabel = {
        Breakfast: "A simple, balanced start to the day.",
        Lunch: "A substantial midday combination to keep you fueled through the afternoon.",
        Dinner: "A protein-forward option to close out the day.",
        "Bedtime Snack": "A light, easy-to-digest option to close the day's meal plan.",
        "Mid-Morning Snack": "A light bite to keep energy steady between breakfast and lunch.",
        "Afternoon Snack": "A light bite to keep energy steady between lunch and dinner.",
      };

      return (
        bySlotLabel[meal?.slotLabel] ||
        "A simple option within today's personalized plan."
      );
    };

  mealCards.forEach(
    (meal, index) => {
      const ingredients = getIngredientNames(meal);
      const whyText = getWhyItFits(meal, index);

      // Bug fix: cardH used to be a fixed 116pt regardless of how much
      // text actually needed to fit inside it. The ingredients line and
      // the "why it fits" line are drawn at fixed offsets (y+59, y+96)
      // that never moved even when either wrapped to 2-3 lines — so a
      // meal with several ingredients or a longer description would spill
      // its text past the card's bottom edge and straight into the next
      // card's content. This measures the actual wrapped line counts
      // first, so both the offsets and the card height reflect what's
      // really being drawn.
      const ingredientsLines = ingredients
        ? measureWrappedLineCount(ingredients, contentWidth - 84, 7.5, "normal")
        : 0;
      const whyLines = measureWrappedLineCount(whyText, contentWidth - 118, 7.2, "normal");

      const ingredientsExtra = ingredients ? (ingredientsLines - 1) * 10 : 0;
      const nutritionYOffset = 78 + ingredientsExtra;
      const whyLabelYOffset = nutritionYOffset + 18;
      const whyExtra = (whyLines - 1) * 9;
      const cardH = Math.max(116, whyLabelYOffset + whyExtra + 20);

      // Pagination: a fixed-position layout never checked whether a card
      // still fit on the current page — with only 4 fixed-height cards it
      // happened to always fit, but a taller (or 5th/6th) card could run
      // past the bottom margin unnoticed. Start a fresh page rather than
      // let that happen silently.
      if (y + cardH > pageHeight - 70) {
        doc.addPage();
        drawPageBackground();
        drawBrandMark(marginX, 30, 128, 38);
        y = 105;
      }

      roundedCard(
        marginX,
        y,
        contentWidth,
        cardH,
        brand.white,
        11,
        brand.border
      );

      // Gold accent
      setFill(brand.gold);

      doc.roundedRect(
        marginX,
        y,
        4,
        cardH,
        2,
        2,
        "F"
      );

      // Number badge
      roundedCard(
        marginX + 12,
        y + 13,
        32,
        32,
        brand.sageTint,
        16
      );

      drawText(
        String(index + 1).padStart(2, "0"),
        marginX + 28,
        y + 34,
        9,
        "bold",
        brand.sageDark,
        {
          align: "center",
        }
      );

      const x =
        marginX + 58;

      drawText(
        (
          meal?.slotLabel ||
          "MEAL"
        ).toUpperCase(),
        x,
        y + 22,
        7.5,
        "bold",
        brand.gold
      );

      drawText(
        meal?.time || "—",
        pageWidth - marginX - 14,
        y + 22,
        7.5,
        "bold",
        brand.muted,
        {
          align: "right",
        }
      );

      drawText(
        meal?.name || "Meal",
        x,
        y + 43,
        13,
        "bold",
        brand.charcoal
      );

      if (ingredients) {
        wrapText(
          ingredients,
          x,
          y + 59,
          contentWidth - 84,
          7.5,
          "normal",
          brand.muted,
          10
        );
      }

      const nutrition = [
        meal?.calories != null
          ? `${meal.calories} kcal`
          : null,
        meal?.proteinG != null
          ? `${meal.proteinG}g protein`
          : null,
        meal?.carbsG != null
          ? `${meal.carbsG}g carbs`
          : null,
        meal?.fatG != null
          ? `${meal.fatG}g fat`
          : null,
      ]
        .filter(Boolean)
        .join("   ·   ");

      drawText(
        nutrition ||
          "Nutrition details unavailable",
        x,
        y + nutritionYOffset,
        8.2,
        "bold",
        brand.sageDark
      );

      drawText(
        "WHY IT FITS",
        x,
        y + whyLabelYOffset,
        6.8,
        "bold",
        brand.muted
      );

      wrapText(
        whyText,
        x + 50,
        y + whyLabelYOffset,
        contentWidth - 118,
        7.2,
        "normal",
        brand.charcoal,
        9
      );

      y += cardH + 9;
    }
  );

  y += 3;

  // Same reasoning as the per-card check above — the meal-card loop can
  // now end close to the bottom of the page, so this summary card needs
  // its own overflow guard rather than assuming it always follows on the
  // same page.
  const MEAL_RHYTHM_CARD_H = 64;
  if (y + MEAL_RHYTHM_CARD_H > pageHeight - 70) {
    doc.addPage();
    drawPageBackground();
    drawBrandMark(marginX, 30, 128, 38);
    y = 105;
  }

  // Meal rhythm
  roundedCard(
    marginX,
    y,
    contentWidth,
    MEAL_RHYTHM_CARD_H,
    brand.soft,
    10
  );

  drawText(
    "MEAL RHYTHM",
    marginX + 14,
    y + 20,
    7.5,
    "bold",
    brand.sageDark
  );

  const rhythm =
    mealCards
      .map((meal) => meal?.time)
      .filter(Boolean)
      .join("   →   ");

  drawText(
    rhythm ||
      "Follow the meal timings shown in your plan.",
    marginX + 14,
    y + 40,
    9.2,
    "bold",
    brand.charcoal
  );

  drawText(
    "A clear rhythm makes the plan easier to follow consistently.",
    marginX + 14,
    y + 54,
    7.5,
    "normal",
    brand.muted
  );

  // See the note above drawFooter(1) — handled in the final pass instead.

  // ============================================================
  // PAGE 3
  // ============================================================

  doc.addPage();

  drawPageBackground();

  drawBrandMark(
    marginX,
    30,
    128,
    38
  );

  y = 105;

  y = drawSectionHeading(
    "Your Daily Wellness Guide",
    "Small habits that support a more consistent day.",
    y
  );

  const defaultTips = [
    "Drink a glass of water before each meal — it helps with portion control.",
    "Try to keep at least a 2-hour gap between dinner and bedtime.",
    "Add a source of protein to every meal to help you stay full longer.",
  ];

  const wellnessTips =
    tips.length
      ? tips.slice(0, 3)
      : defaultTips;

  wellnessTips.forEach(
    (tip, index) => {
      const labels = [
        "HYDRATION",
        "SLEEP & DIGESTION",
        "PROTEIN",
      ];

      const title =
        labels[index] ||
        `WELLNESS TIP ${index + 1}`;

      roundedCard(
        marginX,
        y,
        contentWidth,
        76,
        brand.white,
        10,
        brand.border
      );

      roundedCard(
        marginX + 12,
        y + 18,
        36,
        36,
        brand.sageTint,
        18
      );

      drawText(
        String(index + 1).padStart(2, "0"),
        marginX + 30,
        y + 41,
        9,
        "bold",
        brand.sageDark,
        {
          align: "center",
        }
      );

      drawText(
        title,
        marginX + 62,
        y + 28,
        7.5,
        "bold",
        brand.sageDark
      );

      wrapText(
        tip,
        marginX + 62,
        y + 47,
        contentWidth - 82,
        9,
        "normal",
        brand.charcoal,
        12
      );

      y += 86;
    }
  );

  y += 4;

  drawText(
    "A NOTE FROM YOGASHASTRA AI",
    marginX,
    y,
    10,
    "bold",
    brand.sageDark
  );

  y += 12;

  roundedCard(
    marginX,
    y,
    contentWidth,
    75,
    brand.sageTint,
    10
  );

  wrapText(
    "Your plan is intentionally simple: focus on the meals, keep your hydration steady, and build consistency one day at a time.",
    marginX + 16,
    y + 28,
    contentWidth - 32,
    10,
    "italic",
    brand.sageDark,
    15
  );

  y += 95;

  drawText(
    "QUICK REFERENCE",
    marginX,
    y,
    10,
    "bold",
    brand.sageDark
  );

  y += 12;

  const quickGap = 8;

  const quickW =
    (contentWidth -
      quickGap * 3) /
    4;

  const quick = [
    [
      "DAILY CALORIES",
      hs.calories != null
        ? `${hs.calories} kcal`
        : "—",
    ],
    [
      "PROTEIN",
      hs.proteinG != null
        ? `${hs.proteinG} g`
        : "—",
    ],
    [
      "WATER",
      hs.waterL != null
        ? `${hs.waterL} L`
        : "—",
    ],
    [
      "GOAL",
      goal,
    ],
  ];

  quick.forEach(
    (item, index) => {
      const x =
        marginX +
        index *
          (quickW + quickGap);

      roundedCard(
        x,
        y,
        quickW,
        62,
        brand.white,
        9,
        brand.border
      );

      drawText(
        item[0],
        x + quickW / 2,
        y + 20,
        6.8,
        "bold",
        brand.muted,
        {
          align: "center",
        }
      );

      wrapText(
        item[1],
        x + 7,
        y + 42,
        quickW - 14,
        9,
        "bold",
        brand.charcoal,
        10
      );
    }
  );

  y += 88;

  drawText(
    "YOGASHASTRA",
    marginX,
    y,
    11,
    "bold",
    brand.sageDark
  );

  drawText(
    "AI Diet Planner",
    marginX,
    y + 18,
    14,
    "bold",
    brand.charcoal
  );

  const disclaimer =
    plan?.disclaimer ||
    DEFAULT_DISCLAIMER ||
    "This plan is generated using rule-based nutrition guidelines and AI recommendations. It is not a substitute for professional medical or dietary advice.";

  wrapText(
    disclaimer,
    marginX,
    y + 39,
    contentWidth,
    7.5,
    "normal",
    brand.muted,
    10
  );

  drawText(
    "Healthy choices. Conscious living. One day at a time.",
    marginX,
    pageHeight - 54,
    8.5,
    "italic",
    brand.sageDark
  );

  // Bug fix: the last page's footer used to be drawn with a hardcoded
  // `drawFooter(3)` — correct only because the layout always happened to
  // produce exactly 3 pages. The meal-cards pagination added above (see
  // "text overlap" fix) means a plan with several longer meals can now
  // spill onto an extra page, which a hardcoded "PAGE 3 OF 3" would then
  // mislabel. Drawing every page's footer here, after all content exists,
  // reads the real page count instead of assuming it.
  const totalPages = doc.internal.getNumberOfPages();
  for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
    doc.setPage(pageNumber);
    drawFooter(pageNumber, totalPages);
  }

  // ------------------------------------------------------------
  // RETURN PDF DOCUMENT
  // ------------------------------------------------------------

  return doc;
}
// Policy v1.0 §19 / FSD v2.1 DEC-19: "Do not expose health information
// through public share URLs" / "Use native device/browser share of the
// current PDF or user-approved plan summary. Do not create public
// health-data URLs in v1.0." Builds a short, user-facing text summary
// (goal + top-line targets + disclaimer) that's safe to hand to
// `navigator.share` or the clipboard — never the page URL, since this
// screen's URL is tied to a private session/health record.
export function buildShareSummaryText(plan) {
  const hs = plan?.healthSummary || {};
  const lines = ["My personalized YogaShastra diet plan:"];
  if (hs.goal) lines.push(`Goal: ${toTitleCase(hs.goal)}`);
  if (hs.calories) lines.push(`Target: ${hs.calories} kcal/day`);
  if (hs.proteinG) lines.push(`Protein: ${hs.proteinG} g/day`);
  lines.push("", plan?.disclaimer || DEFAULT_DISCLAIMER);
  return lines.join("\n");
}